import { useState, useEffect } from "react";
import { LayoutGrid, GanttChartSquare, Grid2X2, ArrowRight, Zap, Sparkles, Layers } from "lucide-react";
import { cn } from "@/lib/utils";

const VIEWS = [
  {
    id: "rolls",
    label: "Rolls",
    sub: "Flujo visual",
    icon: LayoutGrid,
    headline: "Visualiza el flujo de tu equipo",
    desc: "Tableros visuales con drag & drop fluido. Mueve tarjetas entre etapas, prioriza al vuelo y mantén a todos alineados sin reuniones extra.",
    bullets: [
      "Columnas personalizables por etapa",
      "Drag & drop con persistencia instantánea",
      "Etiquetas, miembros y fechas en cada tarjeta",
    ],
    accent: "from-blue-500 to-sky-500",
    tone: "blue",
  },
  {
    id: "sprints",
    label: "Sprints",
    sub: "Línea de tiempo",
    icon: GanttChartSquare,
    headline: "Planea el tiempo con precisión",
    desc: "Línea de tiempo nativa para asignar fechas de inicio y fin a cada tarea. Arrastra las barras para reprogramar y visualiza dependencias en una sola pantalla.",
    bullets: [
      "Arrastra para mover · arrastra los bordes para redimensionar",
      "Zoom: 2 semanas · 1 mes · 2 meses · 3 meses",
      "Tareas sin fechas en un cajón listo para programar",
    ],
    accent: "from-violet-500 to-fuchsia-500",
    tone: "violet",
  },
  {
    id: "matrix",
    label: "Matriz",
    sub: "Urgente · Importante",
    icon: Grid2X2,
    headline: "Decide qué hacer primero",
    desc: "Matriz de prioridades Urgente / Importante para enfocarte en lo que realmente mueve la aguja. La misma data del board, otra perspectiva.",
    bullets: [
      "4 cuadrantes claros: Hacer ya, Programar, Delegar, Descartar",
      "Marca urgencia e importancia con un click",
      "Cambia de vista sin perder el contexto",
    ],
    accent: "from-amber-500 to-rose-500",
    tone: "amber",
  },
];

export default function ThreeViewsSection() {
  const [active, setActive] = useState("rolls");
  const [paused, setPaused] = useState(false);

  // Auto-cycle every 5s, pause on hover.
  useEffect(() => {
    if (paused) return;
    const order = VIEWS.map(v => v.id);
    const id = setInterval(() => {
      setActive(curr => order[(order.indexOf(curr) + 1) % order.length]);
    }, 5000);
    return () => clearInterval(id);
  }, [paused]);

  const view = VIEWS.find(v => v.id === active);

  return (
    <section
      id="views"
      className="relative py-20 md:py-28 overflow-hidden"
      data-testid="landing-three-views"
    >
      {/* Decorative bg */}
      <div className="absolute inset-0 bg-grid-soft opacity-30 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-blue-500/5 blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-700 dark:text-blue-300 text-[11px] font-semibold uppercase tracking-wider">
            <Sparkles className="h-3.5 w-3.5" /> Único en su categoría
          </span>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight mt-3 leading-[1.05]">
            <span className="text-blue-500">3 vistas</span>, una sola fuente de verdad.
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mt-5 leading-relaxed">
            Sprintroll es la <b>única herramienta</b> que ofrece administrar proyectos con tanta{" "}
            <span className="text-foreground font-semibold">flexibilidad</span>,{" "}
            <span className="text-foreground font-semibold">facilidad</span> y{" "}
            <span className="text-foreground font-semibold">poder</span>. Alterna entre Rolls,
            Sprints y Matriz de prioridades con un click — sin migrar datos, sin perder contexto.
          </p>
        </div>

        {/* Tabs */}
        <div
          className="flex justify-center mt-12 mb-10"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="glass-panel rounded-full p-1.5 inline-flex gap-1" data-testid="views-tabs">
            {VIEWS.map(v => (
              <button
                key={v.id}
                onClick={() => { setActive(v.id); setPaused(true); }}
                data-testid={`views-tab-${v.id}`}
                aria-pressed={active === v.id}
                className={cn(
                  "px-4 sm:px-5 h-11 rounded-full text-sm font-medium inline-flex items-center gap-2 transition-all",
                  active === v.id
                    ? `text-white bg-gradient-to-r ${v.accent} shadow-lg`
                    : "hover:bg-white/40 dark:hover:bg-white/5"
                )}
              >
                <v.icon className="h-4 w-4" />
                <span>{v.label}</span>
                <span className={cn("hidden sm:inline text-[10px] opacity-70", active === v.id ? "text-white/80" : "")}>
                  {v.sub}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Active view */}
        <div
          key={view.id}
          className="grid lg:grid-cols-5 gap-10 items-center animate-fade-rise"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Copy */}
          <div className="lg:col-span-2 space-y-5">
            <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight leading-[1.1]">
              {view.headline}
            </h3>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">{view.desc}</p>
            <ul className="space-y-2.5">
              {view.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <span className={cn(
                    "mt-0.5 h-5 w-5 rounded-full grid place-items-center flex-none text-white text-[10px] font-bold bg-gradient-to-br",
                    view.accent,
                  )}>
                    {i + 1}
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Visual preview */}
          <div className="lg:col-span-3">
            <div className="glass-panel rounded-3xl p-4 md:p-6 shadow-2xl shadow-blue-500/10 ring-1 ring-blue-500/10 min-h-[340px] md:min-h-[420px]">
              {view.id === "rolls" && <RollsPreview />}
              {view.id === "sprints" && <SprintsPreview />}
              {view.id === "matrix" && <MatrixPreview />}
            </div>
          </div>
        </div>

        {/* Bottom rail */}
        <div className="mt-16 grid sm:grid-cols-3 gap-4">
          <Pill icon={Zap} title="Misma data, otra mirada" desc="Cambia entre vistas instantáneamente, sin reconfigurar nada." />
          <Pill icon={Layers} title="Sin migraciones" desc="Las tarjetas que creas en Rolls aparecen en Sprints y Matriz automáticamente." />
          <Pill icon={Sparkles} title="Hecho para equipos" desc="Cada miembro elige la vista con la que mejor trabaja." />
        </div>
      </div>
    </section>
  );
}

function Pill({ icon: Icon, title, desc }) {
  return (
    <div className="glass-card rounded-2xl p-5 hover:-translate-y-1 hover:shadow-lg transition">
      <Icon className="h-5 w-5 text-blue-500" />
      <p className="font-display font-semibold mt-2.5">{title}</p>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{desc}</p>
    </div>
  );
}

/* ────────────────  Preview illustrations  ──────────────── */

function RollsPreview() {
  const cols = [
    { title: "Por hacer", n: 3, cards: [
      { t: "Diseñar onboarding", c: "bg-violet-500/15 text-violet-700 dark:text-violet-300", l: "Diseño" },
      { t: "Integrar pagos", c: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", l: "Backend" },
    ]},
    { title: "En progreso", n: 2, cards: [
      { t: "Refactor sidebar", c: "bg-rose-500/15 text-rose-700 dark:text-rose-300", l: "UI", drag: true },
      { t: "QA mobile", c: "bg-amber-500/15 text-amber-700 dark:text-amber-300", l: "QA" },
    ], highlight: true },
    { title: "Hecho", n: 4, cards: [
      { t: "Auth Google", c: "bg-blue-500/15 text-blue-700 dark:text-blue-300", l: "Auth", done: true },
      { t: "Workspaces", c: "bg-sky-500/15 text-sky-700 dark:text-sky-300", l: "Core", done: true },
    ]},
  ];
  return (
    <div className="grid grid-cols-3 gap-3 h-full">
      {cols.map((col, i) => (
        <div
          key={col.title}
          className={cn(
            "rounded-2xl p-2.5 border",
            col.highlight
              ? "bg-blue-500/10 border-blue-500/30 dark:bg-blue-500/15"
              : "bg-white/55 dark:bg-slate-900/55 border-white/60 dark:border-white/10"
          )}
        >
          <div className="flex items-center justify-between px-1 pb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{col.title}</span>
            <span className="text-[10px] text-muted-foreground">{col.n}</span>
          </div>
          <div className="space-y-2">
            {col.cards.map((card, idx) => (
              <div
                key={card.t}
                className={cn(
                  "bg-white dark:bg-slate-800 rounded-xl p-2 border border-slate-200/80 dark:border-white/10 shadow-sm",
                  card.drag && "animate-float ring-2 ring-blue-500/40"
                )}
                style={{ animationDelay: `${idx * 120}ms` }}
              >
                <span className={cn("inline-block text-[9px] font-medium px-1.5 py-0.5 rounded-full", card.c)}>{card.l}</span>
                <div className={cn("text-[11px] font-medium mt-1.5 leading-tight", card.done && "line-through text-muted-foreground")}>
                  {card.t}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SprintsPreview() {
  const days = ["L", "M", "M", "J", "V", "S", "D", "L", "M", "M", "J", "V"];
  const tasks = [
    { name: "Diseñar onboarding", row: 0, start: 0, span: 4, color: "from-violet-500 to-fuchsia-500" },
    { name: "Integrar pagos",     row: 1, start: 2, span: 6, color: "from-emerald-500 to-green-500", animate: true },
    { name: "QA mobile",          row: 2, start: 5, span: 3, color: "from-amber-500 to-orange-500" },
    { name: "Lanzar landing v2",  row: 3, start: 7, span: 5, color: "from-blue-500 to-sky-500" },
  ];
  const COL_W = 28;
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 dark:text-blue-300 mb-2 px-1 uppercase tracking-wider">
        <span>JUN 2026</span>
        <span className="ml-auto text-[10px] text-muted-foreground normal-case font-normal tracking-normal">2 semanas</span>
      </div>
      <div className="grid grid-cols-[120px_1fr] gap-x-2 text-[10px]">
        <div />
        <div className="flex border-b border-white/40 dark:border-white/10 pb-1 mb-2">
          {days.map((d, i) => (
            <div key={i}
                 className={cn(
                   "text-center text-muted-foreground",
                   (i === 5 || i === 6) && "text-muted-foreground/50",
                   i === 3 && "text-blue-500 font-bold relative"
                 )}
                 style={{ width: COL_W }}>
              {d}
              <div className="text-[9px] mt-0.5">{i + 1}</div>
              {i === 3 && <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-blue-500" />}
            </div>
          ))}
        </div>

        {tasks.map((t, idx) => (
          <div key={t.name} className="contents">
            <div className="text-[11px] py-1.5 truncate font-medium pr-2">{t.name}</div>
            <div className="relative h-7">
              {/* day grid */}
              <div className="absolute inset-0 flex">
                {days.map((_, i) => (
                  <div key={i}
                       className={cn(
                         "border-r border-white/20 dark:border-white/5",
                         (i === 5 || i === 6) && "bg-white/30 dark:bg-slate-900/30"
                       )}
                       style={{ width: COL_W }} />
                ))}
              </div>
              {/* bar */}
              <div
                className={cn(
                  "absolute top-1 h-5 rounded-md bg-gradient-to-r shadow-md",
                  t.color,
                  t.animate && "animate-pulse-dot"
                )}
                style={{
                  left: t.start * COL_W,
                  width: t.span * COL_W - 4,
                  animationDelay: `${idx * 150}ms`,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Today marker line is faked via the col-3 highlight above */}
      <div className="mt-auto pt-3 text-[10px] text-muted-foreground flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse-dot" /> Hoy
        <span className="ml-4">Arrastra una barra para reprogramar</span>
      </div>
    </div>
  );
}

function MatrixPreview() {
  const cells = [
    {
      title: "Urgente · Importante",
      sub: "Hacer ya",
      cls: "bg-rose-500/10 border-rose-500/40",
      dot: "bg-rose-500",
      cards: [
        { t: "Resolver bug login", c: "rose" },
        { t: "Subir parche v1.2", c: "rose" },
      ],
    },
    {
      title: "No urgente · Importante",
      sub: "Programar",
      cls: "bg-amber-500/10 border-amber-500/40",
      dot: "bg-amber-500",
      cards: [
        { t: "Estrategia Q3", c: "amber" },
        { t: "Migrar a Postgres", c: "amber" },
        { t: "OKR del trimestre", c: "amber" },
      ],
      highlight: true,
    },
    {
      title: "Urgente · No importante",
      sub: "Delegar",
      cls: "bg-sky-500/10 border-sky-500/40",
      dot: "bg-sky-500",
      cards: [
        { t: "Responder email cliente", c: "sky" },
      ],
    },
    {
      title: "No urgente · No importante",
      sub: "Descartar",
      cls: "bg-slate-500/10 border-slate-500/40",
      dot: "bg-slate-500",
      cards: [],
    },
  ];
  const COLOR_BG = {
    rose:  "bg-rose-500/15  text-rose-700  dark:text-rose-300",
    amber: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    sky:   "bg-sky-500/15   text-sky-700   dark:text-sky-300",
  };
  return (
    <div className="grid grid-cols-2 gap-3 h-full">
      {cells.map((cell, i) => (
        <div
          key={cell.title}
          className={cn(
            "rounded-2xl p-3 border-2 flex flex-col gap-2",
            cell.cls,
            cell.highlight && "ring-2 ring-amber-500/40 ring-offset-2 ring-offset-transparent animate-float"
          )}
          style={{ animationDelay: `${i * 110}ms` }}
        >
          <div>
            <div className="flex items-center gap-1.5">
              <span className={cn("h-1.5 w-1.5 rounded-full", cell.dot)} />
              <span className="text-[10px] uppercase tracking-wider font-bold">{cell.sub}</span>
            </div>
            <div className="text-[9px] text-muted-foreground leading-tight mt-0.5">{cell.title}</div>
          </div>
          <div className="space-y-1.5 flex-1">
            {cell.cards.length === 0 ? (
              <div className="text-[10px] text-muted-foreground/70 italic">Vacío</div>
            ) : (
              cell.cards.map(c => (
                <div key={c.t} className={cn("rounded-md px-2 py-1 text-[10px] font-medium", COLOR_BG[c.c])}>
                  {c.t}
                </div>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
